import { z } from "zod";

/**
 * Shape of a single mention attached to a chat user message via the AI SDK
 * `data-mention` part. The discriminator is `kind`; `entityId` is what the
 * server resolves; `label` is carried purely for client rendering of past
 * messages and is ignored by server-side resolvers (which always refetch the
 * authoritative name by id).
 *
 * Add a new mentionable kind here, then add the matching resolver in
 * `apps/api/src/mastra/mentions/registry.ts`.
 */
const baseMention = {
  entityId: z.string().min(1),
  label: z.string().min(1),
};

export const MentionPartDataSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("block"), ...baseMention }),
  z.object({ kind: z.literal("board"), ...baseMention }),
  z.object({ kind: z.literal("dataset"), ...baseMention }),
  z.object({ kind: z.literal("datasource"), ...baseMention }),
]);

export type MentionPartData = z.infer<typeof MentionPartDataSchema>;

export type MentionKind = MentionPartData["kind"];

/** All supported mention kinds (in display order). */
export const MENTION_KINDS = [
  "block",
  "board",
  "dataset",
  "datasource",
] as const satisfies readonly MentionKind[];

/**
 * The `type` value used on the AI SDK `UIMessagePart` for a mention. The SDK
 * builds the part as `{ type: "data-mention", id, data: MentionPartData }`.
 */
export const MENTION_PART_TYPE = "data-mention" as const;

/**
 * Hard cap on the number of mentions we serialize per outgoing message. The
 * client clamps before sending; the server-side processor clamps again after
 * dedupe to prevent prompt bloat / abuse.
 */
export const MAX_MENTIONS_PER_MESSAGE = 20;

/**
 * Sentinel root tag used to mark the resolved-mention text part appended to
 * the user message at the request boundary. The server wraps the resolved
 * `<UserMessage>` + `<Mentions>` blocks in `<MentionContext>...</MentionContext>`
 * so the bubble renderer can detect-and-skip that text part on the client
 * (chips remain the only visible representation), while the LLM still reads
 * the full resolved context as plain text.
 */
export const MENTION_CONTEXT_SENTINEL = "<MentionContext>";
